/**
 * @file toolbar-button.tsx
 * @description Компонент кнопки фильтрации для toolbar таблиц в Task Manager приложении, который обеспечивает интерфейс доступа к модальному окну настройки фильтров с визуальным индикатором активных фильтров. Включает поддержку горячих клавиш F2 для быстрого открытия фильтров, отображение badge с количеством активных фильтров, защиту от одновременного открытия нескольких модальных окон и автоматическое отключение кнопки при отсутствии доступных фильтров.
 * @author AI-Generated | Human-Reviewed
 * @created 2025-01-15
 * @updated 2025-09-22
 * @dependencies ['@consta/icons/IconFunnel', '@consta/uikit/Badge', '@components/ui/base/button', '@consta/uikit/useGlobalKeys', 'react', '@features/filter/helper', '@features/filter/types']
 * @related-files ['src/features/filter/components/table-modal/table-modal.component.tsx', 'src/features/filter/helper.ts', 'src/components/ui/button']
 */

import { IconFunnel } from '@consta/icons/IconFunnel';
import { Badge } from '@consta/uikit/Badge';
import { Button } from '@components/ui/base/button';
import { useGlobalKeys } from '@consta/uikit/useGlobalKeys';
import { useMemo } from 'react';
import { getActiveFilters } from '@features/filter/helper';
import { filterComponent } from '@features/filter/types';

interface IToolbarButtonProps {
    /** Обработчик клика по кнопке фильтра */
    onFilterButtonClick?: (show: boolean) => void;
    /** Массив контролов фильтрации */
    filterControls: filterComponent[];
    /** Флаг отображения модального окна фильтра */
    isShowFilterModal: boolean;
    /** Флаг возможности показа модального окна */
    isCanShowModal: boolean;
}

export const ToolbarButton = ({
    onFilterButtonClick,
    filterControls,
    isShowFilterModal,
    isCanShowModal
}: IToolbarButtonProps) => {
    const activeFilters = useMemo(() => getActiveFilters(filterControls), [filterControls]);

    useGlobalKeys({
        F2: () => showFilterModalHandler(true)
    });

    const showFilterModalHandler = (val: boolean) => {
        if (!isShowFilterModal && isCanShowModal && onFilterButtonClick) {
            onFilterButtonClick(val);
        }
    };

    return (
        <div className="filter-button relative flex items-top">
            <Button
                onlyIcon={true}
                iconSize="xs"
                iconLeft={IconFunnel}
                onClick={() => onFilterButtonClick && onFilterButtonClick(true)}
                label="Фильтры (F2)"
                view="ghost"
                disabled={filterControls.length === 0}
            />

            {activeFilters.length > 0 && (
                <Badge
                    className="absolute right-0 top-0 !p-0 w-[1.0585vmin] h-[1.0585vmin] border-0 translate-y-[-50%] translate-x-[50%]"
                    minified
                    status="normal"
                    label="Есть активные фильтры"
                />
            )}
        </div>
    );
};
